import useGetDigitalAssetId from "@/hooks/useGetDigitalAssetId";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { get } from "@github/webauthn-json/browser-ponyfill";
import { useForm, SubmitHandler, UseFormRegister } from "react-hook-form";
import React, { useEffect, useState, useCallback, useContext } from "react";

import { Button } from "@/components/ui/button";
import CredentialsForm from "@/components/Form/Form";
import CredentialsFooter from "@/components/footer/Footer";
import CredentialsHeader from "@/components/header/Header";
import UsernameInput, { UsernameInputChangeSource } from "@/components/username-input/UsernameInput";
import AltchaCaptcha from "@/components/altcha-captcha/AltchaCaptcha";
import AlertRealOtps from "@/components/alert-real-otps/alert-real-otps";
import NeedHelpButton from "@/components/need-help-button/need-help-button";

import { FormError } from "@/models/continente-credentials/common/FormError";
import { UserSignIn } from "@/models/continente-credentials/mobile/UserSignIn";
import { DigitalAsset } from "@/models/continente-credentials/common/DigitalAsset";
import { UserSignInResponse } from "@/models/continente-credentials/mobile/UserSignInResponse";

import { genericService } from "@/services/GenericService";

import { useOrigin } from "@/hooks/useOrigin";
import {
    useCaptcha,
    siteKey,
} from "@/hooks/continente-credentials/useCaptcha";
import { useDeviceId } from "@/hooks/useDeviceId";
import useGetThemeMode from "@/hooks/useGetThemeMode";
import { useApiResponseHandler } from "@/hooks/useApiResponseHandler";
import useFooterDisplayMode from "@/hooks/useFooterDisplayMode";

import { DigitalAssetContext } from "@/contexts/continente-credentials/DigitalAssetContext";
import { UserContext } from "@/contexts/continente-credentials/UserContext";

import { isNumber } from "@/utils/utils";
import { tryCatch } from "@/utils/try-catch";
import { SendUserSessionMessage } from "@/utils/send-message/send-message";
import { validatePortuguesePhoneNumber } from "@/utils/validatePortuguesePhoneNumber";

import { otpService } from "@/services/continente-credentials/common/OtpService";
import { fidoService } from "@/services/continente-credentials/common/Fido2Service";
import { useTheme } from "@/providers/theme-provider";
import { Config } from "@/config/Config";
import ButtonContainer from "@/components/button-container";
import { analyticsService } from "@/services/AnalyticsService";

type Form = UserSignIn & FormError;

export function SignIn({ digitalAssetId }: { digitalAssetId: string }) {
    const history = useHistory();
    const { t } = useTranslation();

    const { setTheme } = useTheme();
    const location = useLocation<UserSignInResponse>();
    const digitalAssetContext = useContext(DigitalAssetContext);
    const { displayMode, excludeAssetKey } = useFooterDisplayMode();
    const theme = useGetThemeMode();
    const { deviceId, confidenceScore } = useDeviceId();
    const [isInitialized, setIsInitialized] = useState(false);

    if (theme) {
        setTheme(theme);
    }

    const { handleResponse } = useApiResponseHandler();
    const returnUrl = new URLSearchParams(useLocation().search).get("ReturnUrl");

    const captcha = useCaptcha(location.state);
    const { resetCaptchaRequirement, requestCaptchaRequirement, flowRequiresCaptcha } = captcha;
    captcha.useGoogleRecaptcha();

    const userContext = useContext(UserContext);

    // const initialIdentifier =
    //     location.state?.properties?.simplePhoneNumber
    //     || userContext.user.phoneNumber
    //     || userContext.user.email
    //     || "";
    // const initialCountryCode =
    //     location.state?.properties?.phoneNumberCountryCode
    //     || userContext.user.phoneNumberCode
    //     || "";
    // const initialUsername =
    //     location.state?.properties?.phoneNumber
    //     || (userContext.user.phoneNumber
    //         ? `${userContext.user.phoneNumberCode ?? ""}${userContext.user.phoneNumber}`
    //         : userContext.user.email)
    //     || "";

    const [loading, setLoading] = useState(false);
    const [showAlertRealOtps, setShowAlertRealOtps] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [countryCode, setCountryCode] = useState('');
    const [labelEmailOrPhoneNumberVisible, setLabelEmailOrPhoneNumberVisible] =
        useState<boolean>(false);

    const origin = useOrigin();

    const initialize = useCallback(async (digitalAssetId: string | null) => {
        setLoading(true);

        const digitalAssetResult = await genericService.get<DigitalAsset>(
            `${Config.ContinenteCredentials.digitalAssetUrl}/${digitalAssetId}`
        );

        if (digitalAssetResult.kind === "success") {
            digitalAssetContext.updateDigitalAsset(
                digitalAssetResult.value as DigitalAsset
            );

            setTimeout(() => {
                setIsInitialized(true)
            }, 100)
        }

        updateUserContext("");

        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const checkFakeOtp = useCallback(async () => {
        if (origin !== "" && origin !== "https://login.continente.pt") {
            const useFakeOTPResult = await otpService.useFakeOTP();

            if (useFakeOTPResult.kind === "success") {
                if (
                    useFakeOTPResult.value.isUsingFakeOtp != null &&
                    !useFakeOTPResult.value?.isUsingFakeOtp
                ) {
                    showWillSendRealOtps();
                }
            }
        }
    }, [origin]);

    useEffect(() => {
        checkFakeOtp();
    }, [origin]);

    const showWillSendRealOtps = () => {
        setShowAlertRealOtps(true);

        setTimeout(() => {
            setShowAlertRealOtps(false);
        }, 3000);
    };

    useEffect(() => {
        setPhoneNumber('');
        setCountryCode('');
        initialize(digitalAssetId);
    }, [initialize, digitalAssetId]);

    const updateUserContext = (username: string) => {
        const isEmail = isNaN(Number(username.substring(1)));
        if (isEmail) {
            userContext.updateUser({
                ...userContext.user,
                phoneNumber: "",
                email: username,
                primaryContact: "email",
            });
        } else {
            userContext.updateUser({
                ...userContext.user,
                email: "",
                phoneNumber: username,
                phoneCountryCode: countryCode,
                phoneNumberCode: countryCode,
                primaryContact: "phoneNumber",
            });
        }
    };

    const formState = useForm<Form>({
        mode: "onSubmit",
        reValidateMode: "onSubmit",
        defaultValues: {
            username: '',
        },
    });

    const {
        register,
        handleSubmit,
        setError,
        setValue,
        clearErrors,
        formState: { errors },
    } = formState;

    useEffect(() => {
        if (history.action === "POP") {
            resetCaptchaRequirement();
        }
    }, [history, location.key, resetCaptchaRequirement]);

    const handleEmailOrPhoneNumberChange = useCallback(
        (
            input: string,
            phoneNumberCode: string,
            source: UsernameInputChangeSource = "user"
        ) => {
            clearErrors();

            const sanitizedInput = input.replace(/\s+/g, "");

            const { phoneNumberCode: userPhoneNumberCode } = userContext.user;

            const value = isNumber(sanitizedInput)
                ? `${userPhoneNumberCode || phoneNumberCode}${sanitizedInput}`
                : sanitizedInput;

            setPhoneNumber(sanitizedInput);
            setCountryCode(userPhoneNumberCode || phoneNumberCode);

            setValue("username", value);
            setLabelEmailOrPhoneNumberVisible(!!value.length);

            if (source !== "prefill") {
                resetCaptchaRequirement();
            }
        },
        [clearErrors, resetCaptchaRequirement, setValue, userContext]
    );

    const handleFidoLogin = async (
        _: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        const assertionOptions = await fidoService.getAssertionOptions(
            digitalAssetId || ""
        );

        switch (assertionOptions.kind) {
            case "success": {
                const assertionCredential = await get(assertionOptions.value);
                const response = await fidoService.validateAssertion(
                    assertionCredential
                );

                switch (response?.kind) {
                    case "success": {
                        history.push(response.value.nextStep.substring(4));
                    }
                }
            }
        }
    };

    const onSubmit: SubmitHandler<UserSignIn> = async (data) => {
        if (flowRequiresCaptcha && !captcha.isRequired) {
            requestCaptchaRequirement();
            return;
        }

        setLoading(true);

        const { data: response, error } =
            await tryCatch(genericService.post<UserSignInResponse, UserSignIn>(
                Config.ContinenteCredentials.mobileUserSignInUrl,
                {
                    username: data.username,
                    clientId: digitalAssetId || "",
                    returnUrl,
                    phoneNumberCountryCode: isNumber(data.username.replace("+", ""))
                        ? countryCode
                        : undefined,
                    simplePhoneNumber: isNumber(data.username.replace("+", ""))
                        ? phoneNumber
                        : undefined,
                    recaptchaToken: captcha.getCaptchaToken(),
                    userSessionId: location.state?.properties?.userSessionId,
                    deviceId,
                    confidenceScore
                }));

        handleResponse(response, {
            setError,
            onSuccess: (response) => {
                const isPhone = isNumber(data.username.replace("+", ""));
                analyticsService.trackIdentifySubmitted(isPhone ? 'phone' : 'email');

                const isLoginPath = response.value.nextStep.includes('validate-password');
                analyticsService.trackLoginPathDetermined(isLoginPath ? 'login' : 'registration');

                isPhone
                    ? updateUserContext(phoneNumber)
                    : updateUserContext(data.username);

                SendUserSessionMessage(response.value.properties.userSessionId);

                if (response.value.properties.requiredRecaptcha) {
                    history.push({ pathname: location.pathname, search: location.search }, response.value);
                    return;
                }

                history.push(response.value.nextStep.substring(4), response.value);
            },
        }, error);
        setLoading(false);
    };

    useEffect(() => {
        const emailOrPhoneValidator = (register: UseFormRegister<Form>) =>
            register("username", {
                required: {
                    value: true,
                    message: t("common.required_field"),
                },
                pattern: {
                    value:
                        /(^\+?\d*$)|(^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]*$)/,
                    message: t("login_mobile_email.invalid_number_email_input"),
                },
                validate: {
                    valid: (phoneNumber) =>
                        validatePortuguesePhoneNumber(phoneNumber) ||
                        t("common.error_invalid_pt_number"),
                },
            });
        emailOrPhoneValidator(register);
    }, [register, t]);

    if (!isInitialized) {
        return <></>
    }

    return (
        <div>
            <div className="flex justify-center">
                {showAlertRealOtps && (
                    <AlertRealOtps
                        type={"error"}
                        message={"Alerta: envio de SMS e Email ativo!"}
                    />
                )}
            </div>
            <CredentialsForm
                id="RegistrationUser"
                autoComplete="on"
                form={formState}
                onSubmit={(e) => {
                    clearErrors();
                    handleSubmit(onSubmit)(e);
                }}
            >
                <CredentialsHeader
                    title={t("login_mobile_email.header_title")}
                    subtitle={t("login_mobile_email.header_message")}
                    showLoading={loading}
                />
                <UsernameInput
                    ariaLabel={t("accessibility.signin.input")}
                    type="email-phone"
                    isFilled={labelEmailOrPhoneNumberVisible}
                    isError={!!(errors.username || errors.blocked || errors.submitError)}
                    errorMsg={
                        errors.username?.message ||
                        errors.blocked?.message ||
                        errors.submitError?.message ||
                        ""
                    }
                    onChange={handleEmailOrPhoneNumberChange}
                    disabled={loading}
                    onInvalid={() =>
                        setError("username", { message: t("common.required_field") })
                    }
                    value={phoneNumber}
                    autocompleteWith="email"
                />
                {captcha.shouldShowRecaptcha && (
                    <div
                        id="g-recaptcha"
                        className="flex justify-center"
                        data-sitekey={siteKey}
                    />
                )}
                {captcha.shouldShowAltcha && (
                    <AltchaCaptcha
                        onVerified={captcha.handleAltchaCaptchaVerified}
                        onError={captcha.handleAltchaCaptchaError}
                    />
                )}
                <ButtonContainer>
                    <Button disabled={loading}>
                        {t("login_mobile_email.button_next_message")}
                    </Button>
                    {digitalAssetContext.digitalAsset.passskeysEnabled && (
                        <Button
                            type="button"
                            variant={"secondary"}
                            onClick={handleFidoLogin}
                            disabled={loading}>
                            {"Login Passkeys"}
                        </Button>
                    )}
                </ButtonContainer>
                <NeedHelpButton />
                {displayMode !== "none" && (
                    <div
                        role="note"
                        aria-label={t(displayMode === "carousel" ? "accessibility.signin.icons_carousel" : "accessibility.signin.icons")} className="flex flex-col">
                        <CredentialsFooter
                            contextualInformationVisible
                            displayMode={displayMode}
                            excludeAssetKey={excludeAssetKey}
                        />
                    </div>
                )}
                {/* <label>{import.meta.env.DEV ? "Ambiente de DEV" : "Diferente de DEV"}</label>
                    <label>{import.meta.env.BASE_URL ? "BASE_URL: " + import.meta.env.BASE_URL : ""}</label>
                    <label>{import.meta.env.MODE ? "Mode: " + import.meta.env.MODE : ""}</label>
                    <label>{import.meta.env.PROD ? "Ambiente de PROD" : "Diferente de PROD"}</label>
                    <label>{import.meta.env.SSR ? "SSR: " + import.meta.env.SSR : "SSR sem nada"}</label> */}
            </CredentialsForm>
        </div>
    );
}

export const AuthStartQueryPage = () => {
    const digitalAssetId = useGetDigitalAssetId();

    if (!digitalAssetId) {
        console.log("Digital Asset ID is required but was not found in route parameters.");
        return <></>
    }
    return <SignIn digitalAssetId={digitalAssetId} />;
};


export const AuthStartRoutePage = () => {
    let params = useParams<{ clientId: string }>();
    const digitalAssetId = params.clientId;

    if (!digitalAssetId) {
        console.log("Digital Asset ID is required but was not found in route parameters.");
        return <></>
    }

    return <SignIn digitalAssetId={digitalAssetId} />;
};